import { useLazyQuery, useReactiveVar } from "@apollo/client";
import { useSearch } from "@tanstack/react-router";

// biome-ignore lint/style/useNodejsImportProtocol: fine to use as type
import type { ParsedUrlQuery } from "querystring";
import { useEffect, useMemo } from "react";
import InfiniteScroll from "react-infinite-scroll-component";

import LoadingAnimation from "@/components/loading-animation";
import { FacetTypeEnum } from "@/components/pagination";
import useAuth from "@/hooks/use-auth";
import usePageLoading from "@/hooks/use-page-loading";
import { user as userStore } from "@/store";

import CategoriesFilter from "../components/categories-filter";
import SortButton from "../components/sort-button";
import {
  DEFAULT_SORT_OPTION,
  SORT_OPTIONS_CONFIG,
  WISHLIST_SORT_OPTIONS_CONFIG,
} from "../components/sort-button/constants";
import SubcategoriesFilter from "../components/subcategories-filter";
import Layout from "../layout";
import {
  type FacetsDataType,
  GameSortOptionsEnum,
  type GamesDataType,
  type GamesVariablesType,
  type PaginatedGames,
  type UserType,
} from "../types";
import Empty, { EmptyTypeEnum } from "./empty";
import Game from "./game";
import { QUERY } from "./graphql";

export const PAGE_SIZE = 10;

export function selectedSortOption(
  query: ParsedUrlQuery,
  defaultSortOption: GameSortOptionsEnum = DEFAULT_SORT_OPTION
) {
  const { sortBy } = query;

  if (typeof sortBy !== "string") {
    return defaultSortOption;
  }

  if (!(sortBy in GameSortOptionsEnum)) {
    return defaultSortOption;
  }

  return sortBy as GameSortOptionsEnum;
}

export interface GamesProps {
  facets: FacetsDataType;
  games: PaginatedGames;
  user: UserType;
  variables: GamesVariablesType;
}

export default function Games({
  facets: facetsData,
  games: serverGames,
  user: serverUser,
  variables: initialVars,
}: GamesProps) {
  const currentUser = useReactiveVar(userStore);
  const query = useSearch({ strict: false }) as ParsedUrlQuery;
  const filterSlug = (initialVars.filters?.categorySlugs || [""])[0];
  const [category, subcategory] = filterSlug
    ? filterSlug.split("/")
    : [undefined, undefined];
  const { isMe, isUser, queryContext } = useAuth();
  const [fetchUserGames, { data, fetchMore, loading }] = useLazyQuery<
    GamesDataType,
    GamesVariablesType
  >(QUERY);

  const variables = useMemo(() => {
    const vars = {
      ...initialVars,
      isSSR: false,
      isUser,
      sortBy: selectedSortOption(query, initialVars?.sortBy),
    };

    if (vars.sortBy === GameSortOptionsEnum.MY_RATING && !isUser) {
      vars.sortBy = (initialVars?.sortBy ??
        DEFAULT_SORT_OPTION) as GameSortOptionsEnum;
    }

    return vars;
  }, [initialVars, isUser, query]);

  const handleLoadMore = () => {
    const currentPage = data?.games.page || serverGames.page;
    if (!loading) {
      fetchMore({ variables: { ...variables, page: currentPage + 1 } });
    }
  };

  useEffect(() => {
    fetchUserGames(queryContext(variables));
  }, [queryContext, fetchUserGames, variables]);

  const hasMorePages = useMemo(() => {
    if (loading) {
      return false;
    }
    if (data) {
      return !!data.games.hasNext;
    }
    return !!serverGames.hasNext;
  }, [loading, data, serverGames]);
  const title = useMemo(
    () => (variables.escapedOnly ? "Experiences" : "Wishlist"),
    [variables.escapedOnly]
  );
  const games = useMemo(
    () => (data?.games || serverGames).objects,
    [data, serverGames]
  );

  const facets = useMemo(
    () =>
      facetsData.facets?.find((facet) => facet.type === FacetTypeEnum.CATEGORY)
        ?.items || [],
    [facetsData.facets]
  );
  const categoryFacets = useMemo(
    () => facets.filter((facet) => !facet.parentId),
    [facets]
  );
  const allCount = useMemo(
    () => categoryFacets.reduce((accum, facet) => accum + facet.count, 0),
    [categoryFacets]
  );
  const subcategoryFacets = useMemo(() => {
    if (!category) {
      return [];
    }

    const parent = facets.find(
      (facet) => !facet.parentId && facet.slug === category
    );

    if (!parent) {
      return [];
    }

    return facets.filter((facet) => facet.parentId === parent.id);
  }, [facets, category]);

  const urlPrefix = useMemo(
    () =>
      `/@${serverUser.username}/${variables.escapedOnly ? "experiences" : "wishlist"}`,
    [variables.escapedOnly, serverUser.username]
  );

  const sortHref = useMemo(() => {
    let href = urlPrefix;

    const categorySlug = (variables.filters?.categorySlugs || [""])[0];

    if (categorySlug) {
      href += `/${categorySlug}`;
    }

    href += "?sortBy=";

    return href;
  }, [urlPrefix, variables.filters?.categorySlugs]);

  const pageLoading = usePageLoading();

  return (
    <Layout title={title} user={data?.user || serverUser}>
      <div className="flex flex-col md:flex-col-reverse">
        <div>
          <CategoriesFilter
            count={allCount}
            facets={categoryFacets}
            selectedCategory={category}
            urlPrefix={urlPrefix}
          />
          {!!subcategoryFacets.length && (
            <SubcategoriesFilter
              facets={subcategoryFacets}
              selectedCategory={subcategory}
              urlPrefix={`${urlPrefix}/${category}`}
            />
          )}
        </div>
        <div className="mb-5 flex h-7 items-center justify-between md:h-7.5">
          <h1 className="hidden font-medium text-18/[1.5em] text-white md:block">
            {title}
          </h1>
          {!!games.length && (
            <div className="ml-auto flex items-center gap-2">
              <p className="hidden font-medium text-12/[1.5em] text-white md:block">
                Sort by
              </p>
              <SortButton
                href={sortHref}
                isCurrentUser={isMe(serverUser)}
                options={
                  variables.escapedOnly
                    ? SORT_OPTIONS_CONFIG
                    : WISHLIST_SORT_OPTIONS_CONFIG
                }
                sortBy={variables.sortBy as GameSortOptionsEnum}
              />
            </div>
          )}
        </div>
      </div>
      {(() => {
        if (pageLoading) {
          return <LoadingAnimation />;
        }
        if (games.length) {
          return (
            <InfiniteScroll
              dataLength={games.length}
              hasMore={hasMorePages}
              loader={<LoadingAnimation />}
              next={handleLoadMore}
            >
              <section className="flex flex-col items-stretch">
                {games.map((game, index) => (
                  <Game
                    game={game}
                    isFirst={index === 0}
                    key={game.id}
                    showRating={
                      variables.sortBy === GameSortOptionsEnum.USER_RATING
                    }
                  />
                ))}
              </section>
            </InfiniteScroll>
          );
        }
        return (
          <Empty
            isCurrentUser={currentUser?.id === serverUser.id}
            type={
              variables.escapedOnly
                ? EmptyTypeEnum.PLAYED_GAMES
                : EmptyTypeEnum.WISHLIST
            }
            username={serverUser.username}
          />
        );
      })()}
    </Layout>
  );
}
